/**
 * The person's own internet access, kept by the sandbox gateway (vfs-service src/egress/service.js, flow-helm-chart
 * docs/egress.md), under the vfs path the web app already uses, with the person's authorized fetch:
 * `GET /api/v1/me/egress?days=7` -> `{ available, policy: { mode: open|allowlist|registries, domains }, summary, since,
 * hosts: [{ host, port, reason, count, last }], events }`: what their plan lets their sandbox reach, and what Flow's
 * egress proxy blocked recently.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { shareInflight } from '../utils/sharedRequest';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');

/** The person's internet access and blocked requests, or null where the gateway does not have it (an older one). */
export function getMyEgress(days = 7) {
  return shareInflight(`my-egress-${days}`, async() => {
    if (!base()) { return null; }
    const response = await getAuthorizedFetch()(`${base()}/api/v1/me/egress?days=${encodeURIComponent(days)}`, { headers: { Accept: 'application/json' } });
    if (response.status === 404) { return null; }
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(data?.error || `Request failed (${response.status})`);
      error.status = response.status;
      throw error;
    }
    return data && typeof data === 'object' ? data : null;
  });
}

const REASONS = { not_allowed: 'not allowed by your plan', private: 'a private network address', port: 'a mail port' };

/**
 * What the Logs view says (null: nothing to say: open internet, or nothing blocked): `{ title, hosts: [{ host, text }],
 * more }` with the hosts most blocked first (at most `max`).
 */
export function blockedNotice(egress, max = 5) {
  if (!egress || !egress.available || !egress.policy || egress.policy.mode === 'open') { return null; }
  const hosts = Array.isArray(egress.hosts) ? egress.hosts : [];
  if (!hosts.length) { return null; }
  const total = hosts.reduce((sum, h) => sum + (Number(h.count) || 0), 0);
  return {
    title: `Flow blocked ${total} internet request${total === 1 ? '' : 's'} from your sandbox in the last 7 days`,
    hosts: hosts.slice(0, max).map((h) => ({
      host: h.port && h.port !== 443 && h.port !== 80 ? `${h.host}:${h.port}` : h.host,
      text: `${h.count} × ${REASONS[h.reason] || 'blocked'}`,
    })),
    more: Math.max(0, hosts.length - max),
    summary: egress.summary || null,
  };
}
